import React, { FunctionComponent, useCallback, useState } from 'react';

import { clearNotifications } from '@ps-int/ps-components/src/notifications/slice';
import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { ArrowLeft } from '@yandex-360/iconostasis/dist/icons/ArrowLeft';
import { Calendar } from '@yandex-360/iconostasis/dist/icons/Calendar';
import { Filter } from '@yandex-360/iconostasis/dist/icons/Filter';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { ListBox } from '@yandex-360/orb-components/dist/components/ListBox';
import { ListBoxItem, ListBoxItemProps } from '@yandex-360/orb-components/dist/components/ListBoxItem';
import * as Modal from '@yandex-360/orb-components/dist/components/Modal2';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../shared/helpers/i18n';
import { replace, SCREENS } from '../../../../../../shared/helpers/navigation';
import { useActions } from '../../../../../../shared/hooks';
import { ARTIFACTS_FILTER_OPTION, DATE_RANGE_TYPE } from '../../../../constants';
import { useArtifactsOptions } from '../../../../helpers/get-artifacts-options';
import { selectDateRangeType, selectFilterArtifacts } from '../../../../selectors';
import { FilterDatePicker } from '../../../filter/components/filter-date-picker';
import { FilterPeriodPicker } from '../../../filter/components/filter-period-picker';
import { MonthsListBox } from '../../../filter/components/months-list-box';
import { RangeTypeListBox } from '../../../filter/components/range-type-list-box';
import {
    setFilterArtifacts,
    setFilterDateRangeType,
    setFilterFromTo,
    setFilterSelectedMonth,
} from '../../../filter/slice';
import styles from './styles.css';

export const OneColHeader: FunctionComponent = () => {
    const [isArtifactFilterOpen, setIsArtifactFilterOpen] = useState(false);
    const artifactsSelectOptions = useArtifactsOptions();
    const selectedArtifacts = useSelector(selectFilterArtifacts);
    const actions = useActions({
        setFilterDateRangeType,
        setFilterArtifacts,
        setFilterFromTo,
        setFilterSelectedMonth,
        clearNotifications,
    });
    const onArtifactsChange = useCallback(
        ({ itemKey }: ListBoxItemProps<ARTIFACTS_FILTER_OPTION>) => {
            actions.setFilterArtifacts(itemKey);
            setIsArtifactFilterOpen(false);
        },
        [actions]
    );
    const [isDateRangeTypeFilterOpen, setIsDateRangeTypeFilterOpen] = useState(false);
    const dateRangeType = useSelector(selectDateRangeType);
    const onDateRangeTypeSelected = useCallback(
        (item: ListBoxItemProps<DATE_RANGE_TYPE>) => {
            actions.setFilterDateRangeType(item.itemKey);
            setIsDateRangeTypeFilterOpen(false);
            if (item.itemKey === DATE_RANGE_TYPE.FOR_3_MONTH) {
                actions.setFilterFromTo({ from: null, to: null });
            }
        },
        [actions]
    );
    const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
    const onMonthSelected = useCallback(
        ({ itemKey }: ListBoxItemProps<Date>) => {
            actions.setFilterDateRangeType(DATE_RANGE_TYPE.SPECIFIC_MONTH);
            actions.setFilterSelectedMonth(itemKey);
            setIsDateRangeTypeFilterOpen(false);
            setIsMonthFilterOpen(false);
            const to = new Date(itemKey);
            to.setMonth(to.getMonth() + 1);
            to.setDate(to.getDate() - 1);
            actions.setFilterFromTo({ from: dateToString(itemKey, 'YYYY-MM-DD'), to: dateToString(to, 'YYYY-MM-DD') });
        },
        [actions]
    );
    const onMonthClose = useCallback(() => {
        setIsDateRangeTypeFilterOpen(false);
        setIsMonthFilterOpen(false);
    }, []);
    const onMonthBack = useCallback(() => {
        setIsMonthFilterOpen(false);
    }, []);
    return (
        <>
            <div className={styles.oneColHeader}>
                <Button
                    onClick={() => {
                        actions.clearNotifications();
                        replace(SCREENS.CREATE_CALL);
                    }}
                    icon={<ArrowLeft variant="outline" size="sm" />}
                    view="contrast-transparent"
                />
                <Text as="h1" className={styles.title} typography="heading-sm">
                    {i18n('conferences-history', 'conferences-history')}
                </Text>
                <Button
                    icon={<Filter variant="outline" size="sm" />}
                    view="contrast-transparent"
                    onPress={() => setIsArtifactFilterOpen(true)}
                    isChecked={selectedArtifacts !== ARTIFACTS_FILTER_OPTION.ALL_CONFS}
                />
                <Modal.Modal visible={isArtifactFilterOpen} onClose={() => setIsArtifactFilterOpen(false)}>
                    <Modal.NavigationBar title={i18n('conferences-history', 'filter')} closable />
                    <Modal.Slot>
                        <ListBox className={styles.listBox}>
                            {artifactsSelectOptions.map((option) => (
                                <ListBoxItem
                                    key={option.itemKey}
                                    itemKey={option.itemKey}
                                    label={option.label}
                                    isSelected={option.itemKey === selectedArtifacts}
                                    onItemSelect={onArtifactsChange}
                                />
                            ))}
                        </ListBox>
                    </Modal.Slot>
                </Modal.Modal>

                <Button
                    icon={<Calendar variant="outline" size="sm" />}
                    view="contrast-transparent"
                    onPress={() => setIsDateRangeTypeFilterOpen(true)}
                    isChecked={dateRangeType !== DATE_RANGE_TYPE.FOR_3_MONTH}
                />
                <Modal.Modal visible={isDateRangeTypeFilterOpen} onClose={() => setIsDateRangeTypeFilterOpen(false)}>
                    <Modal.NavigationBar title={i18n('conferences-history', 'filter')} closable />
                    <Modal.Slot>
                        <RangeTypeListBox
                            onDateRangeTypeSelected={onDateRangeTypeSelected}
                            toggleIsMonthFilterOpen={() => setIsMonthFilterOpen(!isMonthFilterOpen)}
                        />
                    </Modal.Slot>
                </Modal.Modal>
                <Modal.Modal visible={isMonthFilterOpen} onClose={onMonthClose}>
                    <Modal.NavigationBar
                        title={i18n('conferences-history', 'month')}
                        closable
                        back
                        onBack={onMonthBack}
                    />
                    <Modal.Slot>
                        <MonthsListBox onMonthSelected={onMonthSelected} />
                    </Modal.Slot>
                </Modal.Modal>
            </div>
            {dateRangeType === DATE_RANGE_TYPE.SPECIFIC_DATES && <FilterPeriodPicker />}
            {dateRangeType === DATE_RANGE_TYPE.SPECIFIC_DATE && <FilterDatePicker />}
        </>
    );
};
